import { useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as authApi from "@/api/auth.api";
import { mutationMeta } from "@/lib/query-client";
import type { LoginRequest } from "@/types";
import { AuthContext } from "@/providers/auth-context";
import type { AuthContextValue } from "@/providers/auth-context";

const AUTH_FALLBACK: AuthContextValue = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  clinicalRole: null,
  hasRole: () => false,
  login: async () => ({ mfaRequired: false }),
  selectOrg: async () => ({ mfaRequired: false }),
  completeMFA: async () => {},
  completeMFASetup: async () => {},
  loginWithMicrosoft: async () => {},
  completeEntraLogin: async () => ({ mfaRequired: false }),
  logout: async () => {},
  refreshAuth: async () => null,
};

/** Convenience hook - returns the full AuthContext value. Throws if used outside AuthProvider. */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Safe variant - returns a no-op auth object instead of throwing when provider is missing. */
export function useAuthSafe() {
  return useContext(AuthContext) ?? AUTH_FALLBACK;
}

export function useLogin() {
  return useMutation({
    mutationFn: (req: LoginRequest) => authApi.login(req),
    meta: mutationMeta.silent,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.logout(),
    meta: mutationMeta.silent,
    onSuccess: () => queryClient.clear(),
  });
}

export function useProfile() {
  return useQuery({
    queryKey: ["auth", "profile"],
    queryFn: () => authApi.getProfile(),
  });
}

export function useRefresh() {
  return useMutation({
    mutationFn: () => authApi.refreshToken(),
    meta: mutationMeta.silent,
  });
}

export function useVerifyMFA() {
  return useMutation({
    mutationFn: ({ mfaToken, code }: { mfaToken: string; code: string }) =>
      authApi.verifyMFA(mfaToken, code),
    meta: mutationMeta.silent,
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: ({
      oldPassword,
      newPassword,
    }: {
      oldPassword: string;
      newPassword: string;
    }) => authApi.changePassword(oldPassword, newPassword),
    meta: mutationMeta.silent,
  });
}

export function useLogoutAll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.logoutAll(),
    meta: mutationMeta.silent,
    onSuccess: () => queryClient.clear(),
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) => authApi.requestPasswordReset(email),
    meta: mutationMeta.silent,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: authApi.UpdateProfileRequest) => authApi.updateProfile(req),
    meta: mutationMeta.silent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "profile"] });
    },
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  const { refreshAuth } = useAuth();
  return useMutation({
    mutationFn: (preferences: Record<string, unknown>) =>
      authApi.updatePreferences(preferences),
    meta: mutationMeta.silent,
    onSuccess: async () => {
      await refreshAuth();
      queryClient.invalidateQueries({ queryKey: ["auth", "profile"] });
    },
  });
}
